import { useContext, useEffect, useState, useMemo } from "react";
import { ModalContext } from "../../../utils/modal/ModalProvider";
import styles from "./PageDetailModal.module.css";
import { IAM_SERVER_AUTH_TOKEN_POLICY, IAM_SERVER_SERVICE, IAM_SERVER_SERVICE_PAGE, IAM_SERVER_SERVICE_PAGEAUTH } from "../../../utils/Config";
import axios from "../../../utils/api/axios";
import Switch from "@mui/material/Switch";
import { containsXss } from "../../../utils/security/XssFilter";
import { InnocodeButton, InnocodeInput, InnocodeDropdown, InnocodeDatatable, useDialog, useInnocodeLoadingOverlay } from "@intsoft/innocode-core";



/**
 * 페이지 상세정보 모달 Container
 * @param id
 * @param update
 * @returns {JSX.Element}
 * @constructor
 */
export const PageDetailModal = ({ id, update, pageNameMap }) => {
    const { closeModal } = useContext(ModalContext);
    const { showDialog } = useDialog();
    const { show: showLoading, hide: hideLoading } = useInnocodeLoadingOverlay();
    const [isNull, setIsNull] = useState(false); //빈 값 확인
    const [isDuplicateChecked, setIsDuplicateChecked] = useState(false); // 중복 확인 완료 여부
    const [originalPageUrl, setOriginalPageUrl] = useState(''); // 초기 페이지 URL 저장

    const [formData, setFormData] = useState({
        pageId: '',
        serviceId: '',
        pageName: '',
        pageUrl: '',
        parentPageId: ''
    });

    const [formAuthDataList, setFormAuthDataList] = useState([]);

    useEffect(() => {
        showLoading();
        // id로 서비스 정보 조회
        const url = IAM_SERVER_SERVICE_PAGE + "/get/" + id;
        axios.get(url)
            .then((res) => {

                const initialPageUrl = res.data.data.pageUrl;

                setFormData({
                    pageId: res.data.data.pageId,
                    serviceId: res.data.data.serviceId,
                    pageName: res.data.data.pageName,
                    pageUrl: initialPageUrl,
                    parentPageId: res.data.data.parentPageId
                });

                setOriginalPageUrl(initialPageUrl); // 초기 URL 저장
                setIsDuplicateChecked(true);

                //page 권한 상세 정보 조회
                const pageAuthUrl = IAM_SERVER_SERVICE_PAGEAUTH + "/get/authAll/" + id;
                axios.get(pageAuthUrl)
                    .then((pageAuthRes) => {

                        setFormAuthDataList(pageAuthRes.data.data);

                    })
                    .catch((e) => {
                        showDialog({
                            iconType: "warning",
                            type: 'error',
                            title: "실패",
                            message: "권한 정보 조회에 실패했습니다.",
                            confirmText: "확인",
                        });
                    })
            })
            .catch((e) => {
                showDialog({
                    iconType: "warning",
                    type: 'error',
                    title: "실패",
                    message: "페이지 정보 조회에 실패했습니다.",
                    confirmText: "확인",
                });
            })
            .finally(() => {
                hideLoading();
            })
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));
        if (name === 'pageUrl') {
            setIsDuplicateChecked(false); // pageUrl이 변경되면 중복 확인 다시 해야함
        }
    }


    // 사용자 상세정보 수정 요청 API
    const handleModify = () => {
        const url = IAM_SERVER_SERVICE_PAGE + "/update";
        const params = Object.assign({}, formData);

        for (let key in formData) {
            const value = formData[key];

            if (key !== 'parentPageId' && value === '') {
                setIsNull(true);
                return;
            }

            // 숫자는 검사 안함
            if (key !== 'parentPageId' && typeof value === 'string' && containsXss(value)) {
                setFormData((prevState) => ({
                    ...prevState,
                    [key]: ''
                }));
                setIsNull(true);
                showDialog({
                    iconType: "warning",
                    type: 'error',
                    title: "경고",
                    message: "올바른 값을 입력해 주세요.",
                    confirmText: "확인",
                });
                return;
            }
        }

        if (!formData.pageUrl.startsWith('/')) {
            showDialog({
                iconType: "warning",
                type: 'error',
                title: "경고",
                message: "페이지 URL은 '/'로 시작해야 합니다.",
                confirmText: "확인",
            });
            return;
        }

        if (!isDuplicateChecked) {
            showDialog({
                iconType: "warning",
                type: 'error',
                title: "중복 확인",
                message: "페이지 URL 중복 확인을 해주세요.",
                confirmText: "확인",
            });
            return;
        }



        axios.post(url, params)
            .then((res) => {
                showDialog({
                    iconType: "check",
                    type: 'success',
                    title: "성공",
                    message: res.data.message,
                    confirmText: "확인",
                    onConfirm: () => {
                        update();
                        closeModal();
                    },
                });
            })
            .catch((e) => {
                showDialog({
                    iconType: "warning",
                    type: 'error',
                    title: "실패",
                    message: e.response.data.resultMessage,
                    confirmText: "확인",
                });
            })
    }


    const handleDelete = () => {
        showDialog({
            iconType: "warning",
            type: 'delete',
            title: "페이지 삭제",
            message: "페이지를 삭제하시겠습니까?",
            confirmText: "삭제",
            cancelText: "취소",
            onConfirm: () => {
                const url = IAM_SERVER_SERVICE_PAGE + "/delete/" + formData.pageId;

                axios.post(url)
                    .then((res) => {
                        showDialog({
                            iconType: "check",
                            type: 'success',
                            title: "성공",
                            message: res.data.message,
                            confirmText: "확인",
                            onConfirm: () => {
                                update();
                                closeModal();
                            },
                        });
                    })
                    .catch((e) => {
                        showDialog({
                            iconType: "warning",
                            type: 'error',
                            title: "실패",
                            message: e.response.data.resultMessage,
                            confirmText: "확인",
                        });
                    })
            },
        });
    }


    const handleDuplicateCheck = () => {
        if (!formData.serviceId) {
            showDialog({
                iconType: "warning",
                type: 'error',
                title: "중복확인",
                message: "서비스 url를 입력해주세요.",
                confirmText: "확인",
            });
            return;
        }

        const url = IAM_SERVER_SERVICE_PAGE + "/exists/url";

        const params = {
            serviceId: formData.serviceId,
            pageUrl: formData.pageUrl,
        };

        axios.post(url, params)
            .then(res => {
                if (res.data.data) {
                    setIsDuplicateChecked(false);
                    showDialog({
                        iconType: "warning",
                        type: 'error',
                        title: "중복확인",
                        message: "이미 사용 중인 url입니다.",
                        confirmText: "확인",
                    });
                } else {
                    setIsDuplicateChecked(true);
                    showDialog({
                        iconType: "check",
                        type: 'success',
                        title: "중복확인",
                        message: "사용 가능한 url입니다.",
                        confirmText: "확인",
                    });
                }
            })
            .catch(err => {
                console.error(err);
                showDialog({
                    iconType: "warning",
                    type: 'error',
                    title: "중복확인",
                    message: "오류가 발생했습니다.",
                    confirmText: "확인",
                });
            });
    };

    // 권한 목록 테이블 컬럼 정의
    const authColumns = useMemo(() => [
        {
            id: 'authorityName',
            header: '권한명',
            accessorKey: 'authorityName',
            enableSorting: false,
            align: 'center',
            cell: ({ row }) => <span>{row.authorityName}</span>,
            width: 200,
        },
        {
            id: 'create',
            header: '쓰기',
            accessorKey: 'create',
            enableSorting: false,
            align: 'center',
            cell: ({ row }) => (
                <div style={{ textAlign: 'center' }}>
                    {row.create ? (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16.6666 5L7.49998 14.1667L3.33331 10" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <span style={{ color: '#d1d5db' }}>-</span>
                    )}
                </div>
            ),

        },
        {
            id: 'read',
            header: '읽기',
            accessorKey: 'read',
            enableSorting: false,
            align: 'center',
            cell: ({ row }) => (
                <div style={{ textAlign: 'center' }}>
                    {row.read ? (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16.6666 5L7.49998 14.1667L3.33331 10" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <span style={{ color: '#d1d5db' }}>-</span>
                    )}
                </div>
            ),
        },
        {
            id: 'update',
            header: '수정',
            accessorKey: 'update',
            align: 'center',
            enableSorting: false,
            cell: ({ row }) => (
                <div style={{ textAlign: 'center' }}>
                    {row.update ? (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16.6666 5L7.49998 14.1667L3.33331 10" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <span style={{ color: '#d1d5db' }}>-</span>
                    )}
                </div>
            ),
        },
        {
            id: 'delete',
            header: '삭제',
            accessorKey: 'delete',
            align: 'center',
            enableSorting: false,
            cell: ({ row }) => (
                <div style={{ textAlign: 'center' }}>
                    {row.delete ? (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16.6666 5L7.49998 14.1667L3.33331 10" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <span style={{ color: '#d1d5db' }}>-</span>
                    )}
                </div>
            ),
        },
    ], []);

    return (
        <div style={{ overflow: "auto" }}>
            <div>
                <ul className={styles.layer__listWrap}>
                    <li className={styles.layer__list}>
                        <p className={styles.layer__listTit}>페이지 명</p>
                        <InnocodeInput
                            type="text"
                            name="pageName"
                            value={formData.pageName}
                            onChange={(e) => handleChange(e)}
                            placeholder="페이지명을 입력하세요"
                        />
                    </li>
                    <li className={styles.layer__list}>
                        <p className={styles.layer__listTit}>페이지 URL</p>
                        <InnocodeInput
                            type="text"
                            name="pageUrl"
                            value={formData.pageUrl}
                            onChange={(e) => handleChange(e)}
                            placeholder="페이지 URL을 입력하세요 (예: /example)"
                        />
                        <InnocodeButton
                            backgroundColor="var(--status-negative, #f83a3a)"
                            onClick={handleDuplicateCheck}
                            disabled={formData.pageUrl === originalPageUrl}>중복확인</InnocodeButton>
                    </li>
                    <li className={styles.layer__list}>
                        <p lang={"ko"} className={styles.layer__listTit}>상위 페이지</p>
                        <InnocodeDropdown
                            placeholder="상위 페이지 선택"
                            value={formData.parentPageId || ''}
                            options={[
                                { value: '', label: ':: 상위 페이지 선택 ::', disabled: false },
                                ...(pageNameMap ? Object.entries(pageNameMap).map(([pageId, pageName]) => ({
                                    value: pageId,
                                    label: pageName,
                                    disabled: false
                                })) : [])
                            ]}
                            onChange={(value) => {
                                handleChange({ target: { name: 'parentPageId', value: value || '' } });
                            }}
                        />
                    </li>
                </ul>

                <div className={styles.layer__sensorBox}>
                    <p className={styles.layer__sensorBox__title}>권한 목록</p>
                    <div className={styles.layer__sensorBox__tableWrap}>
                        <InnocodeDatatable
                            data={formAuthDataList}
                            columns={authColumns}
                            enableTableHeader={true}
                            enableSorting={false}
                            enableCheckbox={false}
                            enablePagination={false}
                            emptyMessage="권한이 없습니다."
                            className={styles.dataTable}
                        />
                    </div>
                </div>

                {isNull ? <div className={`${styles.layer__text} ${styles.warning}`}>빈 칸을 확인해 주세요.</div> : null}
                <div className={styles.layer__sensorBtnBox}>
                    <InnocodeButton
                        size="md"
                        backgroundColor="var(--status-negative, #f83a3a)"
                        onClick={handleDelete}
                        fullWidth={true}
                    >
                        삭제
                    </InnocodeButton>
                    <InnocodeButton
                        size="md"
                        onClick={handleModify}
                        fullWidth={true}
                    >
                        수정
                    </InnocodeButton>
                </div>
            </div>
        </div>
    );
}